import { episodeKey, ManifestEpisode, ManifestSeries } from 'src/domain/data';
import { DataProvider, StorageProvider } from 'src/providers';
import { Icon, LightSwitch, SelectPicker } from 'src/controls';
import * as renderer from 'src/renderers';

class CriticalRoleCompendium {
	private dataProvider: DataProvider = new DataProvider('data/manifest.json');
	private searchStorage = new StorageProvider<string>('q', { local: 'session' });

	private controls = {
		results: null! as HTMLElement,
		seriesSelect: SelectPicker.Null,
		episodeSelect: SelectPicker.Null,
		tagSelect: SelectPicker.Null,
		filterBadges: null! as HTMLElement,
		search: null! as HTMLInputElement,
		permalink: null! as JQuery<HTMLAnchorElement>,
	};

	constructor() {
		this.initControls();
		this.initEventListeners();
		this.initAsync();
	}

	private initControls(): void {
		this.controls.seriesSelect = new SelectPicker('#series-select', 'series', { localStorage: 'series' });
		this.controls.episodeSelect = new SelectPicker('#episode-select', 'episode', { interval: true, localStorage: 'episode' });
		this.controls.tagSelect = new SelectPicker('#tag-select', 'tag', { localStorage: new StorageProvider('tags', { local: 'session' }) });

		this.controls.filterBadges = document.getElementById('filter-wrapper')!;
		this.controls.results = document.getElementById('key-results')!;
		this.controls.search = document.getElementById('search-input') as HTMLInputElement;
		this.controls.permalink = <JQuery<HTMLAnchorElement>>Icon.JQuery('link', { ariaLabel: 'Permalink to current search filter', link: '#', target: '_self' }).appendTo('.options-wrapper');

		LightSwitch.Init('#dark-mode-checkbox', 'light', (theme) => document.documentElement.setAttribute('data-theme', theme))
	}

	async initAsync(): Promise<void> {
		this.controls.results.innerHTML = '<div class="loading">Loading episodes...</div>';
		await this.dataProvider.loadData();
		await this.loadEpisodes();
		this.controls.search.value = this.searchStorage.value ?? '';
		this.render();
		setTimeout(() => this.controls.search.focus(), 50);
	}

	async loadEpisodes(): Promise<void> {
		try {
			this.populateSeriesSelector();
			this.populateEpisodeSelector();
			this.populateTagSelector();
		} catch (error) {
			this.showError(`Failed to load episodes. Make sure you have a data/manifest.json file listing your episode files. Error: ${error}`);
		}
	}

	populateSeriesSelector(): void {
		const series = Array.from(this.dataProvider.data.seriesWithEpisodes.values()).map(s => (s.series));
		this.controls.seriesSelect.setEntries(series.map(s => ({ value: s.key, text: s.title, selectedText: s.shortTitle })));
	}

	populateEpisodeSelector(): void {
		const combinedSet = new Set<{ series: ManifestSeries, episode: ManifestEpisode }>();
		if (this.controls.seriesSelect.values.length === 0) {
			this.dataProvider.data.seriesWithEpisodes.forEach((series) =>
				series.episodes.forEach(episode =>
					combinedSet.add({ series: series.series, episode: episode })));
		} else {
			this.controls.seriesSelect.values.forEach(seriesKey => {
				const series = this.dataProvider.data.seriesWithEpisodes.get(seriesKey)!;
				series.episodes.forEach(episode =>
					combinedSet.add({ series: series.series, episode: episode }));
			});
		}

		const episodes = Array.from(combinedSet);
		const options = episodes.map(({ series, episode }) => ({
			value: episodeKey(series.key, episode.id),
			text: episode.title,
			selectedText: episode.shortTitle,
			group: series.title
		}));
		this.controls.episodeSelect.setEntries(options);
	}

	populateTagSelector(): void {
		this.controls.tagSelect.setEntries(this.dataProvider.data.tags);
	}

	formatEpisodeName(episode: string): string {
		return episode
			.replace(/_/g, ' ')
			.replace(/episode/i, 'Episode')
			.replace(/\b\w/g, l => l.toUpperCase());
	}

	initEventListeners(): void {
		if (this.controls.search) {
			this.controls.search.addEventListener('input', (e) => {
				this.searchStorage.value = (e.target as HTMLInputElement).value.toLowerCase();
				this.updateQueryParams();
				this.render();
			});
			this.controls.search.addEventListener('search', () => {
				this.controls.search.blur();
			})
			this.controls.search.addEventListener('keyup', e => {
				if (e.key === 'Enter') this.controls.search.blur();
			})
		}

		if (this.controls.seriesSelect) {
			this.controls.seriesSelect.onChangeHandler = _ => {
				this.populateEpisodeSelector();
				this.updateQueryParams();
				this.render();
			};
		}

		if (this.controls.episodeSelect) {
			this.controls.episodeSelect.onChangeHandler = _ => {
				this.updateQueryParams();
				this.render();
			};
		}

		if (this.controls.tagSelect) {
			this.controls.tagSelect.onChangeHandler = _ => {
				this.updateQueryParams();
				this.render();
			};
		}
	}

	render(): void {
		const directResultsEl = document.getElementById('key-results')!;
		const relatedResultsEl = document.getElementById('related-results')!;
		const statsEl = document.getElementById('stats')!;
		const directEntries = this.dataProvider.searchEntries({
			searchName: this.searchStorage.value,
			series: new Set(this.controls.seriesSelect.values),
			episodes: new Set(this.controls.episodeSelect.values),
			tags: new Set(this.controls.tagSelect.values),
		});
		const relatedEntries = this.dataProvider.searchEntries({
			excludeKeys: new Set(directEntries.map(e => e.key)),
			searchRelated: this.searchStorage.value,
			series: new Set(this.controls.seriesSelect.values),
			episodes: new Set(this.controls.episodeSelect.values),
			tags: new Set(this.controls.tagSelect.values),
		});

		statsEl.textContent = `Showing ${directEntries.length} direct ${directEntries.length === 1 ? 'match' : 'matches'} and ${relatedEntries.length} related ${relatedEntries.length === 1 ? 'entry' : 'entries'}.`;

		renderer.entities(directEntries, directResultsEl, this.searchStorage.value!);
		renderer.entities(relatedEntries, relatedResultsEl, this.searchStorage.value!);
	}

	showError(message: string): void {
		const errorContainer = document.getElementById('error-container')!;
		errorContainer.innerHTML = `<div class="error">${message}</div>`;
		document.getElementById('key-results')!.innerHTML = '';
	}

	updateQueryParams() {
		const url = new URL(window.location.href);
		if (url.search) {
			url.search = '';
			window.history.pushState({}, '', url);
		}

		const newUrlQueryParams = StorageProvider.QuerySearchParams();
		const newUrl = url.origin + url.pathname + '?' + newUrlQueryParams.toString() + url.hash;
		this.controls.permalink.attr('href', newUrl);
	}
}

// Initialize the app
new CriticalRoleCompendium();